import React, { Component } from 'react';
import { Button, Box, Grid } from '@material-ui/core';
import { toast } from 'react-toastify';
import Dialog from '../../../../commonModules/interface/Dialog';
import Spin from '../../../../commonModules/interface/Spin.jsx';
import InputText from '../../../../commonModules/interface/form-input-inline.jsx';
import Currency from '../../../../commonModules/Currency';

// Prix vierge du formulaire d'adoption.
const PRIX_VIERGE = {
    prix_venteHT: "",
    tva_rate: 0,
    prix_venteTTC: 0,
    reduc_max: 0,
}

// Prix de départ du formulaire. Quand le compte a déjà la fiche du produit,
// c'est qu'il le réactive après l'avoir désactivé : ses prix sont repris, il
// n'a qu'à les confirmer ou les corriger. Une valeur absente ou non numérique
// retombe sur le prix vierge, le formulaire exigeant alors une saisie.
function prixInitial(fiche) {
    if (!fiche) {
        return { ...PRIX_VIERGE }
    }
    const ht = Number(fiche.prix_venteHT) || 0
    const tva = Number(fiche.tva_rate) || 0
    return {
        prix_venteHT: ht > 0 ? ht : PRIX_VIERGE.prix_venteHT,
        tva_rate: tva,
        // Le TTC est toujours déduit du HT et du taux, jamais repris de la
        // fiche : c'est la règle de handlePrix, et le serveur le recalcule de
        // toute façon à l'adoption. Reprendre un TTC rangé afficherait un
        // montant que l'enregistrement ne conserverait pas.
        prix_venteTTC: ht * ((tva / 100) + 1),
        reduc_max: Number(fiche.reduc_max) || 0,
    }
}

/**
 * Pose le prix du compte sur un produit du catalogue et l'ajoute à ses
 * produits.
 *
 * Deux écrans mènent au même geste : le catalogue, où l'on va chercher le
 * produit d'un autre, et la création, quand le libellé saisi désigne un
 * produit qui existe déjà. Un seul dialogue les sert, sans quoi les deux
 * chemins finiraient par ne plus demander la même chose.
 *
 * @param {object} produit fiche du catalogue, au minimum id et name.
 * @param {object} [fiche] fiche que le compte a déjà pour ce produit, quand il
 *        le réactive ; ses prix pré-remplissent le formulaire. Absente pour
 *        une première adoption.
 * @param {function} adoptCatalogueProduct action d'adoption, telle que le
 *        tableau de bord Stock la fournit en props.
 * @param {function} close ferme le dialogue sans rien faire.
 * @param {function} onAdopted appelé après une adoption réussie ; à chaque
 *        écran de décider ce qu'il devient alors.
 */
class AdoptProductDialog extends Component {
    state = {
        prix: prixInitial(this.props.fiche),
        sending: false,
    }

    handlePrix = (e) => {
        const { name, value } = e.target
        let newPrix = Object.assign({}, this.state.prix)
        newPrix[name] = value

        // Le TTC se déduit du HT et du taux, comme dans le formulaire produit.
        // Le laisser saisir séparément créerait un réglage incohérent dès sa
        // naissance ; l'ajuster finement reste possible ensuite, dans la fiche
        // du produit.
        //
        // La valeur n'est pas arrondie : c'est celle que le serveur rangera, et
        // le dialogue l'affiche avec le composant qui la montrera ensuite dans
        // la liste. Un arrondi propre au dialogue annoncerait 2 949 là où la
        // liste montre 2 948,82.
        if (name === "prix_venteHT" || name === "tva_rate") {
            const ht = Number(newPrix.prix_venteHT) || 0
            const tva = Number(newPrix.tva_rate) || 0
            newPrix.prix_venteTTC = ht * ((tva / 100) + 1)
        }

        this.setState({ prix: newPrix })
    }

    prixValide = () => {
        const ht = Number(this.state.prix.prix_venteHT)
        const tva = Number(this.state.prix.tva_rate)
        const reduc = Number(this.state.prix.reduc_max)
        return ht > 0 && tva >= 0 && reduc >= 0
    }

    submit = async () => {
        const { produit, onAdopted } = this.props
        if (!produit || !this.prixValide()) {
            return
        }

        this.setState({ sending: true })

        try {
            const resultat = await this.props.adoptCatalogueProduct(produit.id, {
                prix_venteHT: Number(this.state.prix.prix_venteHT),
                prix_venteTTC: Number(this.state.prix.prix_venteTTC),
                tva_rate: Number(this.state.prix.tva_rate),
                reduc_max: Number(this.state.prix.reduc_max),
            })
            // L'écriture a réussi dans tous les cas : seule la relecture de la
            // fiche peut manquer, et cela ne se répare qu'en actualisant la
            // page.
            if (resultat && resultat.rafraichi === false) {
                toast.warning(produit.name + " a été ajouté à vos produits. Actualisez la page pour le voir apparaître dans la liste.")
            } else {
                toast.info(produit.name + " a été ajouté à vos produits")
            }
            if (onAdopted) {
                onAdopted(produit)
            }
        } catch (err) {
            console.log("[ERROR] adoption produit catalogue ", err)
            toast.error(err.message || "L'ajout du produit a échoué")
            this.setState({ sending: false })
        }
    }

    render() {
        const { produit, close } = this.props

        return (
            <Dialog
                block={true}
                title={"Ajouter à mes produits : " + produit.name}
                close={close}
                submit={
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={this.state.sending || !this.prixValide()}
                        onClick={this.submit}
                    >
                        Ajouter
                    </Button>
                }
            >
                {this.state.sending && <Spin noOverlay />}
                <Box p={1}>
                    <span>
                        La fiche produit reste commune à tous les comptes : seul le prix
                        que vous donnez ici appartient au vôtre.
                    </span>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputText
                            type="number"
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            onChange={this.handlePrix}
                            value={this.state.prix.prix_venteHT}
                            name="prix_venteHT"
                            title="Prix de Vente HT"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputText
                            type="number"
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            onChange={this.handlePrix}
                            value={this.state.prix.tva_rate}
                            name="tva_rate"
                            title="Taux TVA (%)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box p={1}>
                            <small>Prix de Vente TTC</small>
                            <div>
                                <Currency value={this.state.prix.prix_venteTTC} />
                            </div>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputText
                            type="number"
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            onChange={this.handlePrix}
                            value={this.state.prix.reduc_max}
                            name="reduc_max"
                            title="Reduction Maximale"
                        />
                    </Grid>
                </Grid>
            </Dialog>
        )
    }
}

export default AdoptProductDialog;
